import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { formatGetal, isTelling } from '../format';
import { GeenMeting } from './MeasurementValue';
import { PLAKKENDE_KOP, PLAKKENDE_RIJKOP, ScrollableTable } from './ScrollableTable';
import { heeftEigenNaam, PADREGEL, veldnaamVan } from '../veldnaam';
import type { TopVeld, TopVelden, TopVeldenReden, Veldnamen } from '../work-quality.api';

export interface BroadFieldsCardProps {
  topVelden: TopVelden | undefined;
  week: string;
  /** De namen bij de paden — spec-veldnamen. Ontbreekt hij, dan toont de tabel de paden. */
  veldnamen?: Veldnamen;
}

/** Elke reden een eigen zin: "er is geen rapport" en "het rapport was niet te lezen" zijn niet hetzelfde. */
const REDEN_SLEUTEL: Record<TopVeldenReden, string> = {
  geen_weekrapport: 'qa.alle.veldenGeenWeekrapport',
  weekrapport_niet_leesbaar: 'qa.alle.veldenWeekrapportNietLeesbaar',
  geen_topvelden_in_weekrapport: 'qa.alle.veldenGeenTopvelden',
  topvelden_onbruikbaar: 'qa.alle.veldenOnbruikbaar',
};

const KOP = 'whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.03em] text-[var(--foreground-muted)]';
const CEL = 'px-3 py-2.5 align-top text-[13px] text-[var(--foreground)] tabular-nums';

/**
 * Stap 3 van de leesvolgorde: de velden waar iedereen op struikelt.
 *
 * Doorgifte van de bestaande veldenlijst uit het wekelijkse AI-rapport, en dus een **kleinere
 * verzameling velden** dan de rest van deze module: het rapport telt over zijn eigen AI-velden,
 * de veldenlijst in Eigen werk over álle gecorrigeerde velden. Die twee aantallen mogen niet naast
 * elkaar gelegd worden, en de vaste regel onder de kop zegt dat er ook bij — zij is niet
 * weglaatbaar, want zonder haar is dit blok een uitnodiging tot precies die vergelijking.
 *
 * Geen doorklik naar een persoon: dit is een instructiesignaal en geen persoonsdoorsnede.
 */
export function BroadFieldsCard({ topVelden, veldnamen, week }: BroadFieldsCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;

  const rijen = (Array.isArray(topVelden?.velden) ? topVelden.velden : []).filter(
    (rij): rij is TopVeld => rij !== null && typeof rij === 'object' && typeof rij.veld === 'string',
  );
  const bereik = topVelden?.bereikAantalAiVelden;

  function aantal(waarde: unknown) {
    return isTelling(waarde) ? formatGetal(waarde, locale) : <GeenMeting label={t('qa.eigen.nietGemeten')} />;
  }

  return (
    <section data-testid="werkkwaliteit-brede-velden" data-werkkwaliteit-sectie="brede-velden">
      <Card>
        <CardHeader className="pb-3">
          <h2 className="text-[13px] font-semibold text-[var(--foreground-heading)]">
            {t('qa.alle.veldenKop')}
          </h2>
          <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-brede-velden-uitleg">
            {t('qa.ai.struikel.uitleg')}
          </p>
          <p className="text-[12px] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-brede-velden-bereik">
            {isTelling(bereik)
              ? t('qa.alle.veldenBereik', { aantal: formatGetal(bereik, locale) })
              : t('qa.alle.veldenBereikOnbekend')}
          </p>
        </CardHeader>

        {topVelden === undefined || topVelden === null ? (
          // Niets gelezen is niet hetzelfde als "het rapport noemde geen veld". Het blok is
          // optioneel in het antwoord, en die afwezigheid als meting presenteren is dezelfde
          // inversie die deze module overal weigert.
          <CardContent>
            <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-brede-velden-ontbreekt">
              {t('qa.alle.veldenNietInAntwoord')}
            </p>
          </CardContent>
        ) : topVelden.reden !== undefined ? (
          <CardContent>
            <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-brede-velden-reden">
              {t(REDEN_SLEUTEL[topVelden.reden])}
            </p>
          </CardContent>
        ) : rijen.length === 0 ? (
          <CardContent>
            <p className="text-[13px] text-[var(--foreground)]" data-testid="werkkwaliteit-brede-velden-leeg">
              {t('qa.ai.struikel.leeg')}
            </p>
          </CardContent>
        ) : (
          <ScrollableTable naam={t('qa.alle.veldenKop')}>
            <Table data-testid="werkkwaliteit-brede-velden-lijst">
              <TableCaption className="sr-only mt-0">
                {t('qa.alle.veldenBijschrift', { week })}
              </TableCaption>
              <TableHeader>
                <TableRow className="bg-[var(--background-muted)] hover:bg-[var(--background-muted)]">
                  <TableHead className={`${KOP} ${PLAKKENDE_KOP}`} scope="col">{t('qa.ai.struikel.kolomVeld')}</TableHead>
                  <TableHead className={KOP} scope="col">{t('qa.ai.struikel.kolomAanpassingen')}</TableHead>
                  <TableHead className={KOP} scope="col">{t('qa.ai.struikel.kolomLeegGelaten')}</TableHead>
                  <TableHead className={KOP} scope="col">{t('qa.ai.struikel.kolomOnjuist')}</TableHead>
                  <TableHead className={KOP} scope="col">{t('qa.ai.struikel.kolomVerzonnen')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rijen.map((rij) => (
                  <TableRow className="group" data-testid={`werkkwaliteit-breed-veld-${rij.veld}`} key={rij.veld}>
                    <TableHead className={`${CEL} ${PLAKKENDE_RIJKOP} h-auto font-medium text-[var(--foreground)]`} scope="row">
                      {veldnaamVan(rij.veld, veldnamen)}
                      {/* Het pad blijft staan voor wie het veld technisch wil nazoeken — en alleen
                          als het niet al de naam ís, anders zou het er twee keer onder elkaar staan. */}
                      {heeftEigenNaam(rij.veld, veldnamen) ? (
                        <span className={PADREGEL}>
                          {rij.veld}
                        </span>
                      ) : null}
                    </TableHead>
                    <TableCell className={CEL}>{aantal(rij.total)}</TableCell>
                    <TableCell className={CEL}>{aantal(rij.gemist)}</TableCell>
                    <TableCell className={CEL}>{aantal(rij.onjuist)}</TableCell>
                    <TableCell className={CEL}>{aantal(rij.hallucinatie)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Card>
    </section>
  );
}
